import React, { Suspense, lazy, useEffect, useState } from "react";
import "./App.css";
import {
  BrowserRouter as Router,
  Routes,
  Route,
  useLocation,
} from "react-router-dom";
import ScrollTopButton from "./components/scroll-to-top";
import Footer from "./components/footer";
import Header from "./components/header";
import CookieBanner from "./components/cookies";
import { Box, Stack, Typography } from "@mui/material";
import CircularProgress from "@mui/joy/CircularProgress/CircularProgress";
import ChatBotWidget from "./components/chatbot-modal";

// Lazy load components
const NotFoundPage = lazy(() => import("./components/notFound-page"));
const Home = lazy(() => import("./components/home"));
const People = lazy(() => import("./components/people"));
const About = lazy(() => import("./components/about"));
const Contact = lazy(() => import("./components/contact"));
const OurPrivacyPolicy = lazy(() => import("./components/OurPrivacyPolicy"));
const CareersPage = lazy(() => import("./components/careers"));
const Commitments = lazy(() => import("./components/Commitments"));
const SDGSection = lazy(() => import("./components/sdgGoals"));

// ScrollToTop component
const ScrollToTop = () => {
  const { pathname } = useLocation();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);

  return null;
};

// Custom Loading Spinner Component
const InitialLoadingSpinner = () => {
  return (
    <Box
      sx={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        height: '100vh',
        width: '100%',
        background: 'transparent',
      }}
    >
      <CircularProgress 
        sx={{
          color: 'primary.main',
          background: 'linear-gradient(135deg, #16a34a, #2563eb)',
          borderRadius: '50%',
          padding: '2px',
          '& .MuiCircularProgress-circle': {
            stroke: 'url(#gradient)',
          },
        }}
      >
        <svg width="0" height="0">
          <defs>
            <linearGradient id="gradient" x1="0%" y1="0%" x2="100%" y2="100%">
              <stop offset="0%" stopColor="#16a34a" />
              <stop offset="100%" stopColor="#2563eb" />
            </linearGradient>
          </defs>
        </svg>
      </CircularProgress>
    </Box>
  );
};

function App() {
  const [isInitialLoad, setIsInitialLoad] = useState(true);
  const scrollToTop = () => {
    window.scrollTo({
      top: 0,
      behavior: "smooth",
    });
  };

  useEffect(() => {
    const timer = setTimeout(() => {
      setIsInitialLoad(false);
    }, 0); 

    return () => clearTimeout(timer);
  }, []);

  return (
    <Router>
      <div style={{ overflowX: "hidden", fontFamily: "sans-serif" }}>
        {isInitialLoad ? (
          <InitialLoadingSpinner />
        ) : (
          <AppLayout />
        )}
      </div>
      <ScrollTopButton onClick={scrollToTop} />
    </Router>
  );
}

const AppLayout = () => {
  const location = useLocation();
  const isDemoPage = location.pathname === "/demo";

  const [chatOpen, setChatOpen] = useState(false);
  const [showMessageBox, setShowMessageBox] = useState(true); // New state for message box visibility

  // Toggle chat open/close
  const toggleChat = () => {
    setChatOpen(!chatOpen);
  };

  // Close message box
  const handleCloseMessageBox = (e: React.MouseEvent) => {
    e.stopPropagation();
    setShowMessageBox(false);
  };

  return (
    <div style={{ fontFamily: "sans-serif" }}>
      {!isDemoPage && <Header />}
      <ScrollToTop />

      <Suspense fallback={<InitialLoadingSpinner />}>
        <Routes>
          <Route path="*" element={<NotFoundPage />} />
          <Route path="/" element={<Home />} />
          <Route path="/aboutUs" element={<About />} />
          <Route path="/people" element={<People />} />
          <Route path="/contact" element={<Contact />} />
          <Route path="/ourPrivacyPolicy" element={<OurPrivacyPolicy />} />
          <Route path="/careers" element={<CareersPage />} />
          <Route path="/commitments" element={<Commitments />} />
          <Route path="/sdgGoals" element={<SDGSection />} />
        </Routes>
      </Suspense>

      {!isDemoPage && <Footer />}
      {!isDemoPage && <CookieBanner />}

      {/* ✅ GLOBAL CHATBOT WITH STATIC DATA ONLY */}
      {/* {!isDemoPage && (
        <Box
          sx={{
            position: "fixed",
            bottom: "20px",
            right: "20px",
            zIndex: 9999,
          }}
        >
          <ChatBotWidget
            open={chatOpen}
            onClose={() => setChatOpen(false)}
          />
          
          {!chatOpen && (
            <Stack
              direction="row"
              alignItems="center"
              spacing={1}
              onClick={toggleChat}
              sx={{
                cursor: 'pointer',
              }}
            >
              {showMessageBox && (
                <Box
                  sx={{
                    bgcolor: 'white',
                    padding: '12px 16px',
                    boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
                    maxWidth: '200px',
                    border: '1px solid #e2e8f0',
                    position: 'relative',
                  }}
                >
                  <Box
                    onClick={handleCloseMessageBox}
                    sx={{
                      position: 'absolute',
                      top: '-6px',
                      left: '-6px',
                      width: '20px',
                      height: '20px',
                      borderRadius: '50%',
                      bgcolor: '#64748b',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      cursor: 'pointer',
                      color: 'white',
                      fontSize: '12px',
                      fontWeight: 'bold',
                      boxShadow: '0 2px 4px rgba(0, 0, 0, 0.2)',
                      transition: 'all 0.2s ease',
                      '&:hover': {
                        bgcolor: '#475569',
                        transform: 'scale(1.1)',
                      },
                    }}
                  >
                    ×
                  </Box>

                  <Typography 
                    variant="body2" 
                    fontWeight="bold" 
                    sx={{ color: '#22C55E', lineHeight: 1.2 }}
                  >
                    We're Online!
                  </Typography>
                  <Typography 
                    variant="caption" 
                    sx={{ color: '#666', lineHeight: 1.2 }}
                  >
                    How may I help you today?
                  </Typography>
                </Box>
              )}
              <Box
                sx={{
                  width: '60px',
                  height: '60px',
                  borderRadius: '50%',
                  background: 'linear-gradient(to right bottom, #22C55E, #10B981, #3B82F6)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  cursor: 'pointer',
                  boxShadow: '0 4px 20px rgba(0, 0, 0, 0.3)',
                  transition: 'all 0.3s ease',
                  '&:hover': {
                    transform: 'scale(1.1)',
                    boxShadow: '0 6px 25px rgba(0, 0, 0, 0.4)',
                  },
                  animation: 'pulse 2s infinite',
                  '@keyframes pulse': {
                    '0%': {
                      boxShadow: '0 0 0 0 rgba(34, 197, 94, 0.7)',
                    },
                    '70%': {
                      boxShadow: '0 0 0 8px rgba(16, 185, 129, 0.4)',
                    },
                    '100%': {
                      boxShadow: '0 0 0 12px rgba(59, 130, 246, 0)',
                    },
                  },
                }}
              >
                <svg 
                  width="30" 
                  height="30" 
                  viewBox="0 0 24 24" 
                  fill="none" 
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path 
                    d="M20 2H4C2.9 2 2 2.9 2 4V22L6 18H20C21.1 18 22 17.1 22 16V4C22 2.9 21.1 2 20 2Z" 
                    fill="white"
                  />
                </svg>
              </Box>
            </Stack>
          )}
        </Box>
      )} */}
    </div>
  );
};

export default App;